/**
 * Net client (P0-T09/T10 client side): colyseus.js connection to ZoneRoom with a
 * dev token (temporary P0 auth; Supabase replaces it in P2), clock sync, remote
 * avatar updates, and `correct` handling. Token comes from ?token= (minted by
 * `pnpm dev:token`); ?user= sets the display id.
 */
import { Client, getStateCallbacks, type Room } from 'colyseus.js';
import type { ClockSync } from '@kota-nova/shared';
import type { PlayerState, ZoneState } from './zone-schema';
import { uint16ToYaw } from './yaw';

export interface NetHandlers {
  onClock(sync: ClockSync): void;
  onPlayerLeft(id: string): void;
  onCorrect(c: { x: number; y: number; z: number; seq: number }): void;
  onStatus(connected: boolean): void;
}

export interface NetHandle {
  send(type: string, payload: unknown): void;
  readonly userId: string;
  readonly connected: boolean;
  readonly room: Room<ZoneState>;
  /** ms between local clock and the server patch timestamp (latest sample; P0-G03) */
  readonly serverPatchLagMs: number;
  /** p95 of patch lag over the rolling 60 s window (P0-G03 threshold metric) */
  patchLagP95(): number;
  leave(): void;
}

export function readJoinParams(): { token: string; userId: string } | null {
  const p = new URLSearchParams(location.search);
  const token = p.get('token');
  if (!token) return null;
  const userId = p.get('user') ?? 'dev-player';
  return { token, userId };
}

/** In local dev without ?token=, ask the dev server (/dev-token) to mint one. */
export async function fetchDevToken(userId: string): Promise<string | null> {
  // the game server mints tokens in dev/staging; *.vercel.app previews have no
  // server behind /dev-token, and production auth arrives via Supabase in P2 (D-21)
  if (/\.vercel\.app$/.test(location.hostname)) return null;
  try {
    const base = serverUrl().replace(/^ws/, 'http');
    const res = await fetch(`${base}/dev-token?user=${encodeURIComponent(userId)}`);
    if (!res.ok) return null;
    const body = (await res.json()) as { token?: string };
    return body.token ?? null;
  } catch {
    return null;
  }
}

export function serverUrl(): string {
  const p = new URLSearchParams(location.search);
  const override = p.get('server');
  if (override) return override;
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') return 'ws://localhost:2567';
  const proto = location.protocol === 'https:' ? 'wss://' : 'ws://';
  return `${proto}${location.host}`;
}

export function colorOf(look: string): string {
  try {
    const l = JSON.parse(look) as { color?: string };
    return l.color ?? '#d6b56d';
  } catch {
    return '#d6b56d';
  }
}

interface PlayersProxy {
  onAdd(cb: (item: PlayerState, key: string) => void, immediate?: boolean): () => void;
  onRemove(cb: (item: PlayerState, key: string) => void): () => void;
}

export async function connectNet(handlers: NetHandlers): Promise<NetHandle> {
  let params = readJoinParams();
  if (!params) {
    // local dev convenience: fetch a minted token from the dev server
    const userId = new URLSearchParams(location.search).get('user') ?? 'dev-player';
    const token = await fetchDevToken(userId);
    if (token) params = { token, userId };
  }
  if (!params) {
    throw new Error('missing dev token — add ?token=<minted>&user=<name> (see pnpm dev:token)');
  }
  const client = new Client(serverUrl());
  const room: Room<ZoneState> = await client.joinOrCreate('zone', {
    token: params.token,
    zone: 'zone:MID:0',
  });

  room.onMessage('clock', (sync: ClockSync) => handlers.onClock(sync));
  room.onMessage('correct', (c: { x: number; y: number; z: number; seq: number }) => handlers.onCorrect(c));

  // schema 3.x state callbacks: the proxy is callable on an instance; the root for
  // the room state is cb(room.state), whose `players` member is the collection proxy
  const cb = getStateCallbacks(room) as unknown as (instance: ZoneState) => { players: PlayersProxy };
  const playersProxy = cb(room.state);
  const offAdd = playersProxy.players.onAdd(() => void 0, true);
  const offRemove = playersProxy.players.onRemove((p) => handlers.onPlayerLeft(p.id));

  let connected = true;
  room.onLeave(() => {
    connected = false;
    handlers.onStatus(false);
  });

  // clock offset (ping/pong, symmetric-delay estimate) + patch-lag window (P0-G03)
  let offsetMs = 0; // client wall clock minus server wall clock
  let offsetKnown = false; // no lag samples until the first pong (warm-up would skew p95)
  let lastPingSent = 0;
  room.onMessage('pong', (m: { t: number }) => {
    const rtt = performance.now() - lastPingSent;
    offsetMs = Date.now() - (m.t + rtt / 2);
    offsetKnown = true;
  });
  const pingTimer = setInterval(() => {
    lastPingSent = performance.now();
    if (connected) room.send('ping', null);
  }, 5000);
  lastPingSent = performance.now();
  room.send('ping', null);

  // Measure at patch DECODE time (property callback on patchTs): lag =
  // local wall clock converted to server time, minus the server's patch stamp.
  let latestLag = 0;
  let lastPatchTs = 0;
  const lagWindow: { at: number; lag: number }[] = [];
  (window as unknown as { __knRoomState?: unknown }).__knRoomState = room.state; // debug (P0)
  const lagSampler = setInterval(() => {
    const ts = room.state.patchTs;
    if (offsetKnown && ts > 0 && ts !== lastPatchTs) {
      lastPatchTs = ts;
      const lag = Date.now() - offsetMs - ts; // receive-time in server clock terms minus stamp
      latestLag = lag;
      lagWindow.push({ at: performance.now(), lag });
      while (lagWindow.length && performance.now() - (lagWindow[0]?.at ?? 0) > 60_000) lagWindow.shift();
    }
  }, 100);

  return {
    send(type: string, payload: unknown): void {
      if (connected) room.send(type, payload);
    },
    get userId() {
      return params.userId;
    },
    get connected() {
      return connected;
    },
    get serverPatchLagMs() {
      return Math.round(latestLag * 10) / 10;
    },
    patchLagP95(): number {
      if (!lagWindow.length) return 0;
      const s2 = lagWindow.map((x) => x.lag).sort((a, b) => a - b);
      return Math.round((s2[Math.floor(s2.length * 0.95)] ?? s2[s2.length - 1] ?? 0) * 10) / 10;
    },
    room,
    leave(): void {
      connected = false;
      clearInterval(pingTimer);
      clearInterval(lagSampler);
      offAdd();
      offRemove();
      void room.leave(true);
    },
  };
}

/** Call every frame: push current remote transforms into the interpolation buffer. */
export function attachNetPump(
  net: NetHandle,
  selfId: () => string,
  onSample: (id: string, x: number, y: number, z: number, yawRad: number, anim: number, look: string) => void,
): void {
  const active = true;
  const pump = (): void => {
    net.room.state.players.forEach((p) => {
      if (p.id !== selfId()) onSample(p.id, p.x, p.y, p.z, uint16ToYaw(p.yaw), p.anim, p.look);
    });
    if (active) requestAnimationFrame(pump);
  };
  requestAnimationFrame(pump);
}
